import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar, type BarDatum } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { ResponsivePie, type ComputedDatum, type LegendDatum } from '@nivo/pie';
import { ResponsiveScatterPlot } from '@nivo/scatterplot';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { TableCellsIcon } from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { getSourceDisplayName } from '@/modules/search/searchGrid/helpers/sourceTabMetadata';
import Loader from '../../Common/Loader';
import { NoRecordsFound } from '../NoRecordsFound';
import { buildAnalyticsFileStem, downloadAnalyticsPng } from './analyticsDownload';
import {
  CT_COLOR_BUCKETS,
  buildAnalyticsSearchTitle,
  formatMetricNumber,
  getCumulativePublicationsByYear,
  getMetricVsScoreData,
  getScoreDistribution,
  getSummaryCardLabel,
  getSummaryCardValue,
  getTopEntitiesByPapers,
  getTopEntitiesByScore,
  pickRandomScatterLabelIds,
} from './analyticsHelpers';
import { navigateToScoreBucketTable } from './analyticsNavigation';
import { navigateToSelectedEntityTable } from './analyticsNavigation';
import { ScatterPlotLabelsLayer } from './analyticsScatterLabels';
import type { AnalyticsTabKey } from './analyticsConfig';
import type { ScoreDistributionSlice } from './analyticsTypes';
import { ChartPanel, SummaryCard } from './analyticsUi';
import { useAssociationAnalyticsData } from './useAssociationAnalyticsData';
import { useSummaryMatchContext } from '@/modules/search/searchGrid/context/SummaryMatchContext';
import './GdaAnalytics.css';

const CHART_THEME = {
  axis: {
    legend: {
      text: {
        fontSize: 13,
        fontWeight: 600,
        fill: '#14365d',
      },
    },
    ticks: {
      text: {
        fontSize: 11,
        fill: '#4a6078',
      },
    },
  },
  grid: {
    line: {
      stroke: '#e2e8f0',
      strokeWidth: 1,
    },
  },
  legends: {
    text: {
      fontSize: 11,
      fill: '#4a6078',
    },
  },
};

interface AssociationAnalyticsDashboardProps {
  associationTab: AnalyticsTabKey;
  source: string;
  view: string;
  names: Array<{ id: string; name?: string; label?: string }>;
}

function AssociationAnalyticsDashboard({
  associationTab,
  source,
  view,
  names,
}: AssociationAnalyticsDashboardProps) {
  const navigate = useNavigate();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const graphsContentRef = useRef<HTMLDivElement>(null);
  const [entityPickerMaxHeight, setEntityPickerMaxHeight] = useState<number | undefined>(undefined);
  const [selectedEntityId, setSelectedEntityId] = useState<string>(() => names?.[0]?.id ?? '');

  useEffect(() => {
    if (!names?.length) return;
    const stillExists = names.some(item => item.id === selectedEntityId);
    if (!stillExists) setSelectedEntityId(names[0].id);
  }, [names, selectedEntityId]);

  const selectedEntity = useMemo(
    () => names.find(item => item.id === selectedEntityId) ?? names[0],
    [names, selectedEntityId],
  );
  const selectedNames = selectedEntity ? [selectedEntity] : [];

  const { data, isLoading, isEmpty, config, isPanelActive } = useAssociationAnalyticsData(
    source,
    view,
    associationTab,
    selectedNames,
  );
  const summaryMatchContext = useSummaryMatchContext();
  const summaryMatchCardContext = useMemo(() => {
    if (
      !summaryMatchContext.isActive ||
      String(summaryMatchContext.tab ?? '').toUpperCase() !== String(associationTab).toUpperCase()
    ) {
      return null;
    }
    return {
      stats: summaryMatchContext.stats,
      resultTotalCount: summaryMatchContext.resultTotalCount,
    };
  }, [
    associationTab,
    summaryMatchContext.isActive,
    summaryMatchContext.resultTotalCount,
    summaryMatchContext.stats,
    summaryMatchContext.tab,
  ]);
  const [topEntitiesTab, setTopEntitiesTab] = useState<'score' | 'papers'>('score');
  const [metricScoreTab, setMetricScoreTab] = useState('');
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);

  useEffect(() => {
    setTopEntitiesTab('score');
    setMetricScoreTab('');
  }, [associationTab, view]);

  const searchTitle = buildAnalyticsSearchTitle(selectedNames);
  const sourceLabel = getSourceDisplayName(source);
  const currentYear = new Date().getFullYear();

  const showEntityPicker = names.length > 1;
  const entityPickerTitle =
    view === 'DISEASES'
      ? 'Diseases'
      : view === 'GENES'
        ? 'Genes'
        : view === 'VARIANTS'
          ? 'Variants'
          : 'Entities';

  useLayoutEffect(() => {
    const el = graphsContentRef.current;
    if (!el || !showEntityPicker) return;

    const update = () => {
      const height = el.clientHeight || el.getBoundingClientRect().height;
      if (Number.isFinite(height)) {
        setEntityPickerMaxHeight(Math.round(height));
      }
    };

    update();
    requestAnimationFrame(() => update());

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [showEntityPicker, isLoading, isEmpty, selectedEntityId, config, topEntitiesTab, metricScoreTab]);

  const activeScatterTab = useMemo(() => {
    if (!config) return null;
    return config.scatterTabs.find(tab => tab.value === metricScoreTab) ?? config.scatterTabs[0] ?? null;
  }, [config, metricScoreTab]);

  const topByScore = useMemo(() => (config ? getTopEntitiesByScore(data, config, 15) : []), [config, data]);
  const topByPapers = useMemo(() => (config ? getTopEntitiesByPapers(data, config, 15) : []), [config, data]);
  const activeTopEntities = topEntitiesTab === 'score' ? topByScore : topByPapers;
  const topBarData = useMemo(
    () =>
      activeTopEntities.map(entity => ({
        entityId: entity.entityId,
        normalizedScore: entity.normalizedScore,
        numPMIDs: entity.numPMIDs,
        numCTs: entity.numCTs,
        color: entity.color,
        barValue: topEntitiesTab === 'score' ? entity.normalizedScore : entity.numPMIDs,
      })) as BarDatum[],
    [activeTopEntities, topEntitiesTab],
  );

  const publicationTrend = useMemo(() => {
    if (!config?.fields.yearFinal) return [];
    return getCumulativePublicationsByYear(data, config.fields.yearFinal, config.fields.pmids);
  }, [config, data]);

  const scoreDistribution = useMemo(
    () => (config ? getScoreDistribution(data, config.fields.score) : []),
    [config, data],
  );

  const scoreDistributionById = useMemo(
    () => new Map(scoreDistribution.map(slice => [slice.id, slice])),
    [scoreDistribution],
  );

  const activeMetricPoints = useMemo(() => {
    if (!config || !activeScatterTab) return [];
    return getMetricVsScoreData(data, config, activeScatterTab.metricKey, 50);
  }, [activeScatterTab, config, data]);

  const scatterLabelIds = useMemo(
    () =>
      pickRandomScatterLabelIds(
        activeMetricPoints.map(point => point.entityId),
        10,
      ),
    [activeMetricPoints],
  );

  const publicationLineData = useMemo(
    () => [
      {
        id: 'Cumulative publications',
        data: publicationTrend.map(point => ({
          x: String(point.year),
          y: point.cumulative,
        })),
      },
    ],
    [publicationTrend],
  );

  const scatterData = useMemo(
    () => [
      {
        id: 'entities',
        data: activeMetricPoints.map(point => ({
          x: point.x,
          y: point.y,
          entityId: point.entityId,
          showLabel: scatterLabelIds.has(point.entityId),
        })),
      },
    ],
    [activeMetricPoints, scatterLabelIds],
  );

  const fileStem = useMemo(() => buildAnalyticsFileStem(config, searchTitle), [config, searchTitle]);

  const handleDownloadPng = async () => {
    if (!config || isDownloadingPng) return;
    setIsDownloadingPng(true);
    try {
      await downloadAnalyticsPng(dashboardRef.current, `${fileStem}.png`);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  const handleScoreBucketClick = (slice: ScoreDistributionSlice) => {
    if (!slice.value || !config) return;
    navigateToScoreBucketTable(navigate, slice, config);
  };

  const handlePieArcClick = (datum: ComputedDatum<ScoreDistributionSlice>) => {
    handleScoreBucketClick(datum.data);
  };

  const handlePieLegendClick = (legend: LegendDatum<ScoreDistributionSlice>) => {
    const slice = scoreDistributionById.get(String(legend.id));
    if (slice) handleScoreBucketClick(slice);
  };

  if (!config) {
    return <NoRecordsFound />;
  }

  if (!isPanelActive || isLoading) {
    return (
      <div className="gda-analytics-loading">
        <Loader />
      </div>
    );
  }

  if (isEmpty) {
    return <NoRecordsFound />;
  }

  const scatterTabValue = activeScatterTab?.value ?? config.scatterTabs[0]?.value ?? '';
  const resolvedMetricTab = metricScoreTab || scatterTabValue;

  return (
    <div ref={dashboardRef} className="gda-analytics-dashboard mr-2">
      <header className="gda-analytics-header">
        <div className="gda-analytics-header__title-row">
          <h2 className="gda-analytics-header__title">{config.analyticsTitle}</h2>
          <SharedBadge
            text={`Source: ${sourceLabel}`}
            variant="primaryLight"
            size="xs"
            className="gda-analytics-header__source-badge"
          />
        </div>
        <div className="gda-analytics-header__actions">
          <SharedButton
            variant="secondaryOutline"
            size="xs"
            className="gda-analytics-header__download mr-0"
            disabled={isDownloadingPng}
            onClick={handleDownloadPng}
            iconLeft={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            {isDownloadingPng ? 'Downloading...' : 'Download PNG'}
          </SharedButton>
          <SharedButton
            variant="secondaryOutline"
            size="xs"
            className="gda-analytics-header__download"
            disabled={!selectedEntityId}
            onClick={() => navigateToSelectedEntityTable(navigate, view, selectedEntityId)}
            iconLeft={<TableCellsIcon className="h-4 w-4" />}
          >
            View filtered table
          </SharedButton>
        </div>
      </header>

      <div className="gda-analytics-summary">
        {config.summaryCards.map(card => (
          <SummaryCard
            key={card.id}
            label={getSummaryCardLabel(card, currentYear)}
            value={formatMetricNumber(
              getSummaryCardValue(card, data, config, summaryMatchCardContext),
              card.decimals,
            )}
          />
        ))}
      </div>

      <div className="gda-analytics-entity-layout">
        {showEntityPicker ? (
          <aside
            className="gda-analytics-entity-picker"
            style={
              entityPickerMaxHeight != null
                ? {
                    maxHeight: entityPickerMaxHeight,
                    height: entityPickerMaxHeight,
                  }
                : undefined
            }
          >
            <div className="gda-analytics-entity-picker__title">{entityPickerTitle}</div>
            <div className="gda-analytics-entity-picker__list">
              {names.map(item => {
                const label = item.label ?? item.name ?? item.id;
                const isActive = item.id === selectedEntityId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`gda-analytics-entity-picker__item ${
                      isActive ? 'gda-analytics-entity-picker__item--active' : ''
                    }`}
                    onClick={() => setSelectedEntityId(item.id)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </aside>
        ) : null}

        <div className="gda-analytics-entity-layout__content" ref={graphsContentRef}>
          <ChartPanel
            title={config.topBarTitle}
            className="gda-analytics-panel--wide"
            tabs={[
              { label: 'Score', value: 'score' },
              { label: 'Papers', value: 'papers' },
            ]}
            activeTab={topEntitiesTab}
            onTabChange={value => setTopEntitiesTab(value as 'score' | 'papers')}
          >
            <div className="gda-analytics-bar-chart">
              <ResponsiveBar
                data={topBarData}
                keys={['barValue']}
                indexBy="entityId"
                layout="vertical"
                margin={{ top: 16, right: 24, bottom: 72, left: 56 }}
                padding={0.28}
                colors={bar => String(bar.data.color ?? '#3b82c4')}
                theme={CHART_THEME}
                axisBottom={{
                  tickRotation: -45,
                  legend: config.labels.entityAxis,
                  legendPosition: 'middle',
                  legendOffset: 58,
                }}
                axisLeft={{
                  legend: topEntitiesTab === 'score' ? config.labels.scoreAxis : config.labels.papersAxis,
                  legendPosition: 'middle',
                  legendOffset: -48,
                }}
                enableLabel={false}
                borderRadius={5}
                tooltip={({ indexValue, value, data: barData }) => (
                  <div className="gda-analytics-tooltip">
                    <strong>{indexValue}</strong>
                    {topEntitiesTab === 'score' ? (
                      <div>
                        {config.labels.scoreAxis}: {Number(value).toFixed(3)}
                      </div>
                    ) : (
                      <div>
                        {config.labels.papersAxis}: {Number(value).toLocaleString('es-ES')}
                      </div>
                    )}
                    {config.features.ctLegend ? (
                      <div>Clinical trials: {Number(barData.numCTs ?? 0)}</div>
                    ) : null}
                    {topEntitiesTab === 'papers' ? (
                      <div>
                        {config.labels.scoreAxis}: {Number(barData.normalizedScore ?? 0).toFixed(3)}
                      </div>
                    ) : null}
                  </div>
                )}
              />
            </div>
            {config.features.ctLegend ? (
              <div className="gda-analytics-legend">
                {CT_COLOR_BUCKETS.map(bucket => (
                  <span key={bucket.label} className="gda-analytics-legend__item">
                    <span
                      className="gda-analytics-legend__swatch"
                      style={{ backgroundColor: bucket.color }}
                    />
                    {bucket.label}
                  </span>
                ))}
              </div>
            ) : null}
          </ChartPanel>

          <div
            className={`gda-analytics-grid ${
              config.features.publicationTrend ? '' : 'gda-analytics-grid--single'
            }`}
          >
            {config.features.publicationTrend ? (
              <ChartPanel title="Cumulative publications by year">
                <div className="gda-analytics-line-chart">
                  <ResponsiveLine
                    data={publicationLineData}
                    margin={{ top: 16, right: 24, bottom: 52, left: 64 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                    curve="monotoneX"
                    axisBottom={{
                      legend: 'Year',
                      legendPosition: 'middle',
                      legendOffset: 40,
                      tickRotation: -50,
                    }}
                    axisLeft={{
                      legend: 'Cumulative PMIDs',
                      legendPosition: 'middle',
                      legendOffset: -52,
                    }}
                    colors={['#3b82c4']}
                    pointSize={6}
                    pointColor="#ffffff"
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enableGridX={false}
                    theme={CHART_THEME}
                    useMesh
                    tooltip={({ point }) => (
                      <div className="gda-analytics-tooltip">
                        <strong>{point.data.x}</strong>
                        <div>Cumulative: {Number(point.data.y).toLocaleString('es-ES')}</div>
                      </div>
                    )}
                  />
                </div>
              </ChartPanel>
            ) : null}

            <ChartPanel title="Score distribution">
              <p className="gda-analytics-panel__hint">
                Click a segment or legend item to filter the table by score range.
              </p>
              <div className="gda-analytics-pie-chart gda-analytics-pie-chart--clickable">
                <ResponsivePie
                  data={scoreDistribution}
                  margin={{ top: 12, right: 120, bottom: 12, left: 12 }}
                  innerRadius={0.58}
                  padAngle={1.2}
                  cornerRadius={3}
                  activeOuterRadiusOffset={8}
                  colors={{ datum: 'data.color' }}
                  borderWidth={1}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                  enableArcLinkLabels={false}
                  arcLabelsSkipAngle={12}
                  arcLabel={item => (item.value > 0 ? `${item.value}` : '')}
                  theme={CHART_THEME}
                  onClick={handlePieArcClick}
                  tooltip={({ datum }) => (
                    <div className="gda-analytics-tooltip">
                      <strong>{datum.label}</strong>
                      <div>
                        {datum.value} {config.labels.distributionCountNoun}
                      </div>
                      <div className="gda-analytics-tooltip__action">Click to filter table</div>
                    </div>
                  )}
                  legends={[
                    {
                      anchor: 'right',
                      direction: 'column',
                      translateX: 110,
                      itemWidth: 100,
                      itemHeight: 18,
                      symbolSize: 10,
                      symbolShape: 'circle',
                      onClick: handlePieLegendClick,
                    },
                  ]}
                />
              </div>
            </ChartPanel>
          </div>

          {config.features.metricScatter && config.scatterTabs.length ? (
            <ChartPanel
              title={config.topScatterTitle}
              className="gda-analytics-panel--wide"
              tabs={config.scatterTabs.map(tab => ({ label: tab.label, value: tab.value }))}
              activeTab={resolvedMetricTab}
              onTabChange={setMetricScoreTab}
            >
              <div className="gda-analytics-scatter-chart">
                <ResponsiveScatterPlot
                  data={scatterData}
                  layers={['grid', 'axes', 'nodes', ScatterPlotLabelsLayer, 'legends']}
                  margin={{ top: 28, right: 24, bottom: 56, left: 64 }}
                  xScale={{
                    type: 'linear',
                    min: activeScatterTab?.xMin ?? 0,
                    max: activeScatterTab?.xMax ?? 'auto',
                  }}
                  yScale={{ type: 'linear', min: 0, max: config.tab === 'DDA' ? 'auto' : 1 }}
                  axisBottom={{
                    legend: activeScatterTab?.axisLabel ?? '',
                    legendPosition: 'middle',
                    legendOffset: 42,
                  }}
                  axisLeft={{
                    legend: config.labels.scatterY,
                    legendPosition: 'middle',
                    legendOffset: -50,
                  }}
                  colors={['#3b82c4']}
                  nodeSize={10}
                  theme={CHART_THEME}
                  tooltip={({ node }) => (
                    <div className="gda-analytics-tooltip">
                      <strong>{node.data.entityId}</strong>
                      <div>
                        {activeScatterTab?.axisLabel}: {Number(node.data.x).toFixed(3)}
                      </div>
                      <div>
                        {config.labels.scatterY}: {Number(node.data.y).toFixed(3)}
                      </div>
                    </div>
                  )}
                />
              </div>
            </ChartPanel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default AssociationAnalyticsDashboard;
